import { SingleInstanceRunner, type PriorityExecutor } from '@lc/librunner'
import type { Action } from '@reduxjs/toolkit'
import { load, type LoadAPI, type LoaderImpl } from '../load/load'
import { clearLoadingState } from '../loading'
import type { Store } from '../store'

export function track({
	store,
	executor,
	id,
	name = `track(${JSON.stringify(id)})`,
	level,
	loader,
	interval,
}: {
	store: Store
	executor: PriorityExecutor
	id: string
	name?: string
	level: number
	loader: LoaderImpl
	interval: number
}): () => void {
	const impl = SingleInstanceRunner.wrap(async () => {
		store.dispatch(clearLoadingState(id))
		await load({ store, executor, id, name, level, loader })
	})
	void impl()
	const handle = setInterval(() => void impl(), interval)
	return () => {
		clearInterval(handle)
	}
}

export interface Tracker<T> {
	(
		params: {
			store: Store
			executor: PriorityExecutor
			level: number
			interval: number
		} & T,
	): () => void
	id(params: T): string
}

export function makeTracker<T>({
	id,
	loader,
}: {
	id: ((params: T) => string) | string
	loader: (
		api: LoadAPI,
		params: T,
	) => Promise<Action | Action[] | null | void>
}): Tracker<T> {
	if (typeof id === 'string') {
		const realId = id
		id = () => realId
	}
	const built = ({
		store,
		executor,
		level,
		interval,
		...params
	}: {
		store: Store
		executor: PriorityExecutor
		level: number
		interval: number
	}) =>
		track({
			store,
			executor,
			id: id(params as T),
			level,
			interval,
			loader: api => loader(api, params as T),
		})
	return Object.assign(built, { id })
}
